<template>
    <label
        class="iu-checkbox"
        :class="[
            border && checkboxSize ? 'iu-checkbox--' + checkboxSize : '',
            { 'is-disabled': isDisabled },
            { 'is-bordered': border },
            { 'is-checked': isChecked },
        ]"
        :id="id"
    >
        <span
            class="iu-checkbox__input"
            :class="{
                'is-disabled': isDisabled,
                'is-checked': isChecked,
                'is-indeterminate': indeterminate,
                'is-focus': focus,
            }"
            :tabindex="indeterminate ? 0 : false"
            :role="indeterminate ? 'checkbox' : false"
            :aria-checked="indeterminate ? 'mixed' : false"
        >
            <span class="iu-checkbox__inner"></span>
            <input
                v-if="trueLabel || falseLabel"
                class="iu-checkbox__original"
                type="checkbox"
                :aria-hidden="indeterminate ? 'true' : 'false'"
                :name="name"
                :disabled="isDisabled"
                :true-value="trueLabel"
                :false-value="falseLabel"
                v-model="model"
                @change="handleChange"
                @focus="focus = true"
                @blur="focus = false"
            />
            <input
                v-else
                class="iu-checkbox__original"
                type="checkbox"
                :aria-hidden="indeterminate ? 'true' : 'false'"
                :disabled="isDisabled"
                :value="label"
                :name="name"
                v-model="model"
                @change="handleChange"
                @focus="focus = true"
                @blur="focus = false"
            />
        </span>
        <span class="iu-checkbox__label" v-if="$slots.default || label">
            <slot></slot>
            <template v-if="!$slots.default">{{ label }}</template>
        </span>
    </label>
</template>
<script>
import Emitter from '../../../utils/emitter';
export default {
    name: 'IuCheckbox',
    mixins: [Emitter],
    inject: {
        elForm: {
            default: '',
        },
        elFormItem: {
            default: '',
        },
    },
    componentName: 'IuCheckbox',
    data() {
        return {
            selfModel: false,
            focus: false,
            isLimitExceeded: false,
        };
    },
    computed: {
        model: {
            get() {
                return this.isGroup ? this.store : this.value !== undefined ? this.value : this.selfModel;
            },
            set(val) {
                if (this.isGroup) {
                    this.isLimitExceeded = false;
                    this._checkboxGroup.min !== undefined &&
                        val.length < this._checkboxGroup.min &&
                        (this.isLimitExceeded = true);
                    this._checkboxGroup.max !== undefined &&
                        val.length > this._checkboxGroup.max &&
                        (this.isLimitExceeded = true);
                    this.isLimitExceeded === false && this.dispatch('IuCheckboxGroup', 'input', [val]);
                } else {
                    this.$emit('input', val);
                    this.selfModel = val;
                }
            },
        },
        isChecked() {
            if ({}.toString.call(this.model) === '[object Boolean]') {
                return this.model;
            } else if (Array.isArray(this.model)) {
                return this.model.indexOf(this.label) > -1;
            } else if (this.model !== null && this.model !== undefined) {
                return this.model === this.trueLabel;
            }
            return false;
        },
        isGroup() {
            let parent = this.$parent;
            while (parent) {
                if (parent.$options.componentName !== 'IuCheckboxGroup') {
                    parent = parent.$parent;
                } else {
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this._checkboxGroup = parent;
                    return true;
                }
            }
            return false;
        },
        store() {
            return this._checkboxGroup ? this._checkboxGroup.value : this.value;
        },
        /* used to make the isDisabled judgment under max/min props */
        isLimitDisabled() {
            const { max, min } = this._checkboxGroup;
            return (
                (!!(max || min) && this.model.length >= max && !this.isChecked) ||
                (this.model.length <= min && this.isChecked)
            );
        },
        isDisabled() {
            return this.isGroup
                ? this._checkboxGroup.disabled || this.disabled || (this.elForm || {}).disabled || this.isLimitDisabled
                : this.disabled || (this.elForm || {}).disabled;
        },
        _elFormItemSize() {
            return (this.elFormItem || {}).elFormItemSize;
        },
        checkboxSize() {
            const temCheckboxSize = this.size || this._elFormItemSize || (this.$ELEMENT || {}).size;
            return this.isGroup ? this._checkboxGroup.checkboxGroupSize || temCheckboxSize : temCheckboxSize;
        },
    },
    props: {
        value: {},
        label: {},
        indeterminate: Boolean,
        disabled: Boolean,
        checked: Boolean,
        name: String,
        trueLabel: [String, Number],
        falseLabel: [String, Number],
        id: String /* 当indeterminate为真时，为controls提供相关连的checkbox的id，表明元素间的控制关系*/,
        controls: String /* 当indeterminate为真时，为controls提供相关连的checkbox的id，表明元素间的控制关系*/,
        border: Boolean,
        size: String,
    },
    methods: {
        addToStore() {
            if (Array.isArray(this.model) && this.model.indexOf(this.label) === -1) {
                this.model.push(this.label);
            } else {
                this.model = this.trueLabel || true;
            }
        },
        handleChange(ev) {
            if (this.isLimitExceeded) return;
            let value;
            if (ev.target.checked) {
                value = this.trueLabel === undefined ? true : this.trueLabel;
            } else {
                value = this.falseLabel === undefined ? false : this.falseLabel;
            }
            this.$emit('change', value, ev);
            this.$nextTick(() => {
                if (this.isGroup) {
                    this.dispatch('IuCheckboxGroup', 'change', [this._checkboxGroup.value]);
                }
            });
        },
    },
    created() {
        this.checked && this.addToStore();
    },
    mounted() {
        // 为indeterminate元素 添加aria-controls 属性
        if (this.indeterminate) {
            this.$el.setAttribute('aria-controls', this.controls);
        }
    },
    watch: {
        value(value) {
            this.dispatch('IuFormItem', 'iu.form.change', value);
        },
    },
};
</script>

<style scope>
@charset "UTF-8";
.iu-checkbox,
.iu-checkbox__input {
    display: inline-block;
    position: relative;
}
.iu-checkbox-button__inner,
.iu-checkbox__input {
    white-space: nowrap;
    vertical-align: middle;
    outline: 0;
    height: 16px;
}
.iu-checkbox {
    color: #606266;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
    white-space: nowrap;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    margin-right: 30px;
    height: 16px;
}
.iu-checkbox.is-bordered {
    padding: 9px 20px 9px 10px;
    border-radius: 4px;
    border: 1px solid #dcdfe6;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    line-height: normal;
    height: 40px;
}
.iu-checkbox.is-bordered.is-checked {
    border-color: #409eff;
}
.iu-checkbox.is-bordered.is-disabled {
    border-color: #ebeef5;
    cursor: not-allowed;
}
.iu-checkbox.is-bordered + .iu-checkbox.is-bordered {
    margin-left: 10px;
}
.iu-checkbox.is-bordered.iu-checkbox--medium {
    padding: 7px 20px 7px 10px;
    border-radius: 4px;
    height: 36px;
}
.iu-checkbox.is-bordered.iu-checkbox--medium .iu-checkbox__label {
    line-height: 17px;
    font-size: 14px;
}
.iu-checkbox.is-bordered.iu-checkbox--medium .iu-checkbox__inner {
    height: 14px;
    width: 14px;
}
.iu-checkbox.is-bordered.iu-checkbox--small {
    padding: 5px 15px 5px 10px;
    border-radius: 3px;
    height: 32px;
}
.iu-checkbox.is-bordered.iu-checkbox--small .iu-checkbox__label {
    line-height: 15px;
    font-size: 12px;
}
.iu-checkbox.is-bordered.iu-checkbox--small .iu-checkbox__inner {
    height: 12px;
    width: 12px;
}
.iu-checkbox.is-bordered.iu-checkbox--small .iu-checkbox__inner::after {
    height: 6px;
    width: 2px;
}
.iu-checkbox.is-bordered.iu-checkbox--mini {
    padding: 3px 15px 3px 10px;
    border-radius: 3px;
    height: 28px;
}
.iu-checkbox.is-bordered.iu-checkbox--mini .iu-checkbox__label {
    line-height: 12px;
    font-size: 12px;
}
.iu-checkbox.is-bordered.iu-checkbox--mini .iu-checkbox__inner {
    height: 12px;
    width: 12px;
}
.iu-checkbox.is-bordered.iu-checkbox--mini .iu-checkbox__inner::after {
    height: 6px;
    width: 2px;
}
.iu-checkbox__input {
    cursor: pointer;
    line-height: 1;
}
.iu-checkbox__input.is-disabled .iu-checkbox__inner {
    background-color: #edf2fc;
    border-color: #dcdfe6;
    cursor: not-allowed;
}
.iu-checkbox__input.is-disabled .iu-checkbox__inner::after {
    cursor: not-allowed;
    border-color: #c0c4cc;
}
.iu-checkbox__input.is-disabled .iu-checkbox__inner + .iu-checkbox__label {
    cursor: not-allowed;
}
.iu-checkbox__input.is-disabled.is-checked .iu-checkbox__inner {
    background-color: #f2f6fc;
    border-color: #dcdfe6;
}
.iu-checkbox__input.is-disabled.is-checked .iu-checkbox__inner::after {
    border-color: #c0c4cc;
}
.iu-checkbox__input.is-disabled.is-indeterminate .iu-checkbox__inner {
    background-color: #f2f6fc;
    border-color: #dcdfe6;
}
.iu-checkbox__input.is-disabled.is-indeterminate .iu-checkbox__inner::before {
    background-color: #c0c4cc;
    border-color: #c0c4cc;
}
.iu-checkbox__input.is-checked .iu-checkbox__inner,
.iu-checkbox__input.is-indeterminate .iu-checkbox__inner {
    background-color: #409eff;
    border-color: #409eff;
}
.iu-checkbox__input.is-disabled + span.iu-checkbox__label {
    color: #c0c4cc;
    cursor: not-allowed;
}
.iu-checkbox__input.is-checked .iu-checkbox__inner::after {
    -webkit-transform: rotate(45deg) scaleY(1);
    transform: rotate(45deg) scaleY(1);
}
.iu-checkbox__input.is-checked + .iu-checkbox__label {
    color: #409eff;
}
.iu-checkbox__input.is-focus .iu-checkbox__inner {
    border-color: #409eff;
}
.iu-checkbox__input.is-indeterminate .iu-checkbox__inner::before {
    content: '';
    position: absolute;
    display: block;
    background-color: #fff;
    height: 2px;
    -webkit-transform: scale(0.5);
    transform: scale(0.5);
    left: 0;
    right: 0;
    top: 5px;
}
.iu-checkbox__input.is-indeterminate .iu-checkbox__inner::after {
    display: none;
}
.iu-checkbox__inner {
    display: inline-block;
    position: relative;
    border: 1px solid #dcdfe6;
    border-radius: 2px;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    width: 16px;
    height: 16px;
    background-color: #fff;
    z-index: 1;
    -webkit-transition: border-color 0.25s cubic-bezier(0.71, -0.46, 0.29, 1.46),
        background-color 0.25s cubic-bezier(0.71, -0.46, 0.29, 1.46);
    transition: border-color 0.25s cubic-bezier(0.71, -0.46, 0.29, 1.46),
        background-color 0.25s cubic-bezier(0.71, -0.46, 0.29, 1.46);
}
.iu-checkbox__inner:hover {
    border-color: #409eff;
}
.iu-checkbox__inner::after {
    -webkit-box-sizing: content-box;
    box-sizing: content-box;
    content: '';
    border: 1px solid #fff;
    border-left: 0;
    border-top: 0;
    height: 9px;
    left: 5px;
    position: absolute;
    top: 1px;
    -webkit-transform: rotate(45deg) scaleY(0);
    transform: rotate(45deg) scaleY(0);
    width: 3px;
    -webkit-transition: -webkit-transform 0.15s ease-in 0.05s;
    transition: -webkit-transform 0.15s ease-in 0.05s;
    transition: transform 0.15s ease-in 0.05s;
    transition: transform 0.15s ease-in 0.05s, -webkit-transform 0.15s ease-in 0.05s;
    -webkit-transform-origin: center;
    transform-origin: center;
}
.iu-checkbox__original {
    opacity: 0;
    outline: 0;
    position: absolute;
    margin: 0;
    width: 0;
    height: 0;
    z-index: -1;
}
.iu-checkbox-button,
.iu-checkbox-button__inner {
    display: inline-block;
    position: relative;
}
.iu-checkbox__label {
    display: inline-block;
    padding-left: 10px;
    line-height: 19px;
    font-size: 14px;
}
.iu-checkbox:last-of-type {
    margin-right: 0;
}
.iu-checkbox-button__inner {
    line-height: 1;
    font-weight: 500;
    cursor: pointer;
    background: #fff;
    border: 1px solid #dcdfe6;
    border-left: 0;
    color: #606266;
    -webkit-appearance: none;
    text-align: center;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    margin: 0;
    -webkit-transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    padding: 12px 20px;
    font-size: 14px;
    border-radius: 0;
}
.iu-checkbox-button__inner.is-round {
    padding: 12px 20px;
}
.iu-checkbox-button__inner:hover {
    color: #409eff;
}
.iu-checkbox-button__inner [class*='iu-icon-'] {
    line-height: 0.9;
}
.iu-checkbox-button__inner [class*='iu-icon-'] + span {
    margin-left: 5px;
}
.iu-checkbox-button__original {
    opacity: 0;
    outline: 0;
    position: absolute;
    margin: 0;
    z-index: -1;
}
.iu-checkbox-button.is-checked .iu-checkbox-button__inner {
    color: #fff;
    background-color: #409eff;
    border-color: #409eff;
    -webkit-box-shadow: -1px 0 0 0 #8cc5ff;
    box-shadow: -1px 0 0 0 #8cc5ff;
}
.iu-checkbox-button.is-checked:first-child .iu-checkbox-button__inner {
    border-left-color: #409eff;
}
.iu-checkbox-button.is-disabled .iu-checkbox-button__inner {
    color: #c0c4cc;
    cursor: not-allowed;
    background-image: none;
    background-color: #fff;
    border-color: #ebeef5;
    -webkit-box-shadow: none;
    box-shadow: none;
}
.iu-checkbox-button.is-disabled:first-child .iu-checkbox-button__inner {
    border-left-color: #ebeef5;
}
.iu-checkbox-button:first-child .iu-checkbox-button__inner {
    border-left: 1px solid #dcdfe6;
    border-radius: 4px 0 0 4px;
    -webkit-box-shadow: none !important;
    box-shadow: none !important;
}
.iu-checkbox-button.is-focus .iu-checkbox-button__inner {
    border-color: #409eff;
}
.iu-checkbox-button:last-child .iu-checkbox-button__inner {
    border-radius: 0 4px 4px 0;
}
.iu-checkbox-button--medium .iu-checkbox-button__inner {
    padding: 10px 20px;
    font-size: 14px;
    border-radius: 0;
}
.iu-checkbox-button--medium .iu-checkbox-button__inner.is-round {
    padding: 10px 20px;
}
.iu-checkbox-button--small .iu-checkbox-button__inner {
    padding: 9px 15px;
    font-size: 12px;
    border-radius: 0;
}
.iu-checkbox-button--small .iu-checkbox-button__inner.is-round {
    padding: 9px 15px;
}
.iu-checkbox-button--mini .iu-checkbox-button__inner {
    padding: 7px 15px;
    font-size: 12px;
    border-radius: 0;
}
.iu-checkbox-button--mini .iu-checkbox-button__inner.is-round {
    padding: 7px 15px;
}
.iu-checkbox-group {
    font-size: 0;
}
</style>
